import { Box, Button, Divider, Flex, Heading, HStack, Stack, Text } from '@chakra-ui/react';
import NiceModal from '@ebay/nice-modal-react';
import { useQueryClient } from '@tanstack/react-query';
import { DetailPageTask, Task } from 'api/types';
import Link from 'components/Link';
import SystemPartnerCard from 'components/SystemPartnerCard';
import { useUserContext } from 'context/UserContextProvider';
import useCheckTaskMutation from 'hooks/mutations/useCheckTaskMutation';
import { get, isEmpty } from 'lodash';
import TaskBannerCard from 'pages/tasks/TaskBannerCard';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import ActivityLogCard from '../../components/ActivityLogCard';
import { ChoiceSwitch } from '../../components/ChoiceSwitch';
import { IdentificationResultIcon } from '../../components/Icons';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import AlertModal from '../../components/modals/AlertModal';
import { StatusCard } from '../../components/StatusCard';
import { TasksTableCard } from '../../components/TasksTableCard';
import { dateTimeTransformer } from '../../helpers/dateTransformer';
import { downloadFile } from '../../helpers/downloadHelper';
import { displayRow, getStatus } from '../../helpers/general';
import useAssignMutation from '../../hooks/mutations/useAssignMutation';
import useAssignNextTaskMutation from '../../hooks/mutations/useAssignNextTaskMutation';
import useCancelWorkshopUpdateRequestMutation from '../../hooks/mutations/useCancelWorkshopUpdateRequestMutation';
import useUpdateChargeForWorkshopUpdateRequestMutation from '../../hooks/mutations/useUpdateChargeForWorkshopUpdateRequestMutation';
import useUpdateWorkshopUpdateRequestStatus from '../../hooks/mutations/useUpdateWorkshopUpdateRequestStatus';
import useFileQuery from '../../hooks/useFileQuery';
import useWorkshopUpdateRequestQuery from '../../hooks/useWorkshopUpdateRequestQuery';
import { ContentCard } from '../../layout/ContentCard';
import { ContentLayout } from '../../layout/ContentLayout';
import RecentlyCheckedCard from '../tasks/RecentlyCheckedCard';
import WorkshopCard from '../workshop/WorkshopCard';
import WorkshopVerificationTableCard from './WorkshopVerificationTableCard';

const WorkshopUpdateRequestDetailPage = () => {
  const { statusCodes, statusTransitions, user, isMember, opUsers } = useUserContext();
  const { t } = useTranslation();
  const { id, type } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [url] = useState('');
  const [downloadDocument] = useState(undefined);

  const [click, setClick] = useState(false);

  const file = useFileQuery(url);

  const workshopUpdateRequestId = Number(id);
  const workshopUpdateRequestQuery = useWorkshopUpdateRequestQuery(workshopUpdateRequestId);
  const queryToInvalidate = () => queryClient.invalidateQueries({ queryKey: useWorkshopUpdateRequestQuery.getKey(workshopUpdateRequestId) });

  // @ts-ignore
  const workshopUpdateRequestCodes = statusCodes?.workshop_update_request || {};
  const resourceKey = 'workshop_update_request';

  const assignMutation = useAssignMutation(
    workshopUpdateRequestQuery.data?.open_task?.id as number,
    useWorkshopUpdateRequestQuery.getKey(workshopUpdateRequestId),
  );
  const hasOpenTask = workshopUpdateRequestQuery?.data?.open_task != undefined;

  const isSystemPartnerFull = workshopUpdateRequestQuery?.data?.systempartner?.integration === 'full';

  const taskNotInFinalState =
    workshopUpdateRequestQuery.data?.open_task !== null &&
    workshopUpdateRequestQuery.data?.open_task !== undefined &&
    workshopUpdateRequestQuery.data?.open_task?.status > 200 &&
    workshopUpdateRequestQuery.data?.open_task?.status < 700;

  const dateTaskCheckedByUser = workshopUpdateRequestQuery?.data?.open_task?.last_checked_at;

  const checkTaskMutation = useCheckTaskMutation(
    workshopUpdateRequestQuery?.data?.open_task?.id as number,
    useWorkshopUpdateRequestQuery.getKey(workshopUpdateRequestId),
  );

  const updateWorkshopUpdateRequestStatus = useUpdateWorkshopUpdateRequestStatus(
    workshopUpdateRequestId,
    useWorkshopUpdateRequestQuery.getKey(workshopUpdateRequestId),
  );
  const updateChargeForWorkshopUpdateRequestMutation = useUpdateChargeForWorkshopUpdateRequestMutation();
  const cancelWorkshopUpdateRequestMutation = useCancelWorkshopUpdateRequestMutation(
    workshopUpdateRequestId,
    useWorkshopUpdateRequestQuery.getKey(workshopUpdateRequestId),
  );
  const assignNextTaskMutation = useAssignNextTaskMutation(type ?? '');

  // cancelling rejects the request on the workshop's behalf; allowed in any non-final
  // state and without a task (e.g. while on hold waiting for a re-verification)
  const status = workshopUpdateRequestQuery.data?.status;
  const canCancel =
    isMember('workshop-data-update-requests:update') &&
    status !== undefined &&
    status !== getStatus(workshopUpdateRequestCodes, 'resolved') &&
    status !== getStatus(workshopUpdateRequestCodes, 'rejected');

  // the attached re-verification gets deleted on cancel; warn accordingly and link to it.
  // verifications come newest-first; "started" = the workshop already submitted documents
  const latestVerification = (workshopUpdateRequestQuery.data as any)?.verifications?.[0];
  const verificationStarted = latestVerification != null && latestVerification.status !== 0;
  const cancelModalDescription = !latestVerification ? (
    t('workshop_update_request:modals.cancel.description')
  ) : (
    <Trans
      i18nKey={
        verificationStarted
          ? 'workshop_update_request:modals.cancel.description_verification_started'
          : 'workshop_update_request:modals.cancel.description_verification_not_started'
      }
      values={{ verification: latestVerification.user_readable_id }}
      components={{
        verificationLink: (
          <Link to={`/app/op/workshop-verification-request/${latestVerification.id}`} target="_blank" rel="noopener noreferrer" />
        ),
      }}
    />
  );
  const tasks = (workshopUpdateRequestQuery.data as { tasks?: DetailPageTask[] } | undefined)?.tasks;

  useEffect(() => {
    downloadFile(downloadDocument, file);
    setClick(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, click]);

  const oldValues = workshopUpdateRequestQuery.data?.old_values ?? {};
  const newValues = workshopUpdateRequestQuery.data?.new_values ?? {};

  const workshopDataFields = {
    open_task: {
      label: t('forms:open_task.label'),
    },
    id: {
      label: t('forms:id.label'),
    },
    distributor: {
      label: t('workshop:distributor.label'),
    },
    type: {
      label: t('workshop:users.types.label'),
    },
    firstname: {
      label: t('workshop:firstname.label'),
    },
    lastname: {
      label: t('workshop:lastname.label'),
    },
    email: {
      label: t('workshop:email.label'),
    },
    billing_email: {
      label: t('workshop:billing_email.label'),
      info: t('workshop:billing_email.info'),
    },
    disable_cost_control: {
      label: t('workshop:disable_cost_control.label'),
      info: t('workshop:disable_cost_control.info'),
    },
    password: {
      label: t('workshop:password.label'),
    },
    telephone: {
      label: t('workshop:telephone.label'),
      info: t('workshop:telephone.info'),
    },
    name: {
      label: t('workshop:name.label'),
    },
    vat_number: {
      label: t('workshop:vat_number.label'),
    },
    legal_form: {
      label: t('workshop:legal_form.label'),
    },
    country: {
      label: t('workshop:country.label'),
    },
    language: {
      label: t('workshop:language.label'),
    },
    city: {
      label: t('workshop:city.label'),
    },
    zipcode: {
      label: t('workshop:zipcode.label'),
    },
    address: {
      label: t('workshop:address.label'),
    },
    registered_at: {
      label: t('workshop:registered_at.label'),
    },
    activated_at: {
      label: t('workshop:activated_at.label'),
    },
  };

  if (isNaN(workshopUpdateRequestId) || workshopUpdateRequestQuery.isError) {
    navigate('/404', { replace: true });
    return null;
  }

  if (file.isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      {hasOpenTask ? (
        <TaskBannerCard
          queryToInvalidate={queryToInvalidate}
          opUsers={opUsers}
          currentUserId={user?.id}
          assignee={workshopUpdateRequestQuery.data?.open_task?.assignee}
          isLoading={workshopUpdateRequestQuery.isFetching}
          assign={() =>
            assignMutation.mutate({
              updated_at: get(workshopUpdateRequestQuery.data, 'open_task.updated_at', workshopUpdateRequestQuery.data?.updated_at)!,
            })
          }
          reassign={(user_id) =>
            assignMutation.mutate({
              force: true,
              user_id,
              updated_at: get(workshopUpdateRequestQuery.data, 'open_task.updated_at', workshopUpdateRequestQuery.data?.updated_at)!,
            })
          }
          task={workshopUpdateRequestQuery.data?.open_task as Task}
        />
      ) : null}
      <ContentLayout
        sidebar={
          <>
            {type ? (
              <Button
                variant="primary"
                disabled={workshopUpdateRequestQuery.isFetching || !isMember('tasks:assign-next')}
                data-test-id={`workshop_update_request_assign_button`}
                onClick={() =>
                  assignNextTaskMutation.mutate(
                    //@ts-ignore
                    {},
                    {
                      onSuccess: (task: Task) => {
                        if (task?.id) {
                          navigate(`/app/op/workshop-update-request/${task.subject_id}/${type}`);
                        } else {
                          navigate(`/app/op/break`);
                        }
                      },
                    },
                  )
                }
              >
                {t('common:next_activity')}
              </Button>
            ) : null}
            <WorkshopCard
              statusCodes={statusCodes?.workshop}
              isLoading={workshopUpdateRequestQuery.isFetching}
              workshop={workshopUpdateRequestQuery.data?.workshop}
            />
          </>
        }
      >
        <StatusCard
          permission="workshop-data-update-requests:update"
          isLoading={workshopUpdateRequestQuery.isFetching}
          resourceKey={'workshop_update_request'}
          // @ts-ignore
          completedAt={dateTimeTransformer(workshopUpdateRequestQuery.data?.completed_at)}
          // @ts-ignore
          statusCodes={workshopUpdateRequestCodes}
          currentStatus={workshopUpdateRequestQuery.data?.status}
          currentStatusReason={workshopUpdateRequestQuery.data?.status_reason}
          statusTransitions={statusTransitions?.workshop_update_request}
          reasons={statusCodes?.workshop_update_request_reasons}
          subContentOpenByDefault={workshopUpdateRequestQuery.data?.status === getStatus(workshopUpdateRequestCodes, 'clarification')}
          canEdit={Boolean(workshopUpdateRequestQuery.data?.open_task?.user_id === user?.id)}
          showSubContent={!isEmpty(workshopUpdateRequestQuery.data?.conversation)}
          updateStatus={(data: object) =>
            updateWorkshopUpdateRequestStatus.mutateAsync(data).then(() => {
              return queryClient.invalidateQueries();
            })
          }
        />
        {isSystemPartnerFull && <SystemPartnerCard name={workshopUpdateRequestQuery?.data?.systempartner?.name} />}
        {hasOpenTask &&
        taskNotInFinalState &&
        (workshopUpdateRequestQuery.data?.status === getStatus(workshopUpdateRequestCodes, 'on_hold') ||
          workshopUpdateRequestQuery.data?.status === getStatus(workshopUpdateRequestCodes, 'clarification')) ? (
          <RecentlyCheckedCard
            dateTaskCheckedByUser={dateTaskCheckedByUser}
            checkTask={() => {
              // @ts-ignore
              checkTaskMutation.mutate({
                openTaskId: workshopUpdateRequestQuery?.data?.open_task?.id,
              });
            }}
          />
        ) : null}
        <ContentCard
          isLoading={workshopUpdateRequestQuery.isFetching}
          icon={<IdentificationResultIcon boxSize={10} />}
          minH="fit-content"
          header={`${t('workshop_update_request:header')} ${
            workshopUpdateRequestQuery.data?.workshop?.firstname
          } ${workshopUpdateRequestQuery.data?.workshop?.lastname} ( ${workshopUpdateRequestQuery.data?.user_readable_id} )`}
          data-test-id="update-request-detail-card"
        >
          <HStack>
            <Stack w={'50%'}>
              <Heading size={'xxs'}>{t('workshop_update_request:old_values')}</Heading>
              {Object.keys(oldValues).map((key) =>
                displayRow({
                  //@ts-ignore
                  label: workshopDataFields[key]?.label,
                  //@ts-ignore
                  content: oldValues[key],
                  key,
                }),
              )}
            </Stack>
            <Stack>
              <Heading size={'xxs'}>{t('workshop_update_request:new_values')}</Heading>
              {Object.keys(newValues).map((key) =>
                displayRow({
                  //@ts-ignore
                  label: workshopDataFields[key]?.label,
                  //@ts-ignore
                  content: newValues[key],
                  key,
                }),
              )}
            </Stack>
          </HStack>
          <Box>
            <Divider mt={'10px'} mb={'30px'} />
            <Text>{t('workshop_update_request:billing')}</Text>
            <ChoiceSwitch
              mt={'30px'}
              choice1={{ label: t('common:no'), value: '0' }}
              choice2={{
                label: t('common:yes'),
                value: '1',
              }}
              disabled={
                [700, 900].includes(workshopUpdateRequestQuery.data?.status as number) ||
                workshopUpdateRequestQuery.data?.open_task?.user_id !== user?.id
              }
              defaultValue={workshopUpdateRequestQuery.data?.billable.toString()}
              fontWeight="medium"
              _checked={{
                bgColor: 'green.500',
              }}
              width={'258px'}
              color={'blackAlpha.200'}
              bgColor={'blackAlpha.200'}
              border={'1px'}
              onChange={() =>
                updateChargeForWorkshopUpdateRequestMutation
                  .mutateAsync({
                    workshopUpdateRequestId: workshopUpdateRequestId,
                    billable: !workshopUpdateRequestQuery.data?.billable,
                    status: workshopUpdateRequestQuery.data?.status as number,
                  })
                  .then(() => queryClient.invalidateQueries({ queryKey: useWorkshopUpdateRequestQuery.getKey(workshopUpdateRequestId) }))
              }
            />
          </Box>
          {canCancel && (
            <Flex justifyContent="flex-end" mt={'30px'}>
              <Button
                colorScheme="red"
                isLoading={cancelWorkshopUpdateRequestMutation.isPending}
                data-test-id="workshop_update_request_cancel_button"
                onClick={() =>
                  NiceModal.show(AlertModal as any, {
                    onSubmit: () => cancelWorkshopUpdateRequestMutation.mutateAsync(),
                    redSubmitBttn: true,
                    children: cancelModalDescription,
                    content: {
                      header: t('workshop_update_request:modals.cancel.header'),
                      footer: {
                        buttons: {
                          cancelCaption: t('common:cancel'),
                          actionCaption: t('workshop_update_request:modals.cancel.action'),
                        },
                      },
                    },
                  })
                }
              >
                {t('workshop_update_request:cancel_button')}
              </Button>
            </Flex>
          )}
        </ContentCard>

        <WorkshopVerificationTableCard workshopUpdateRequestId={workshopUpdateRequestId} />

        <TasksTableCard tasks={tasks} isLoading={workshopUpdateRequestQuery.isPending} />
        <ActivityLogCard
          account_id={workshopUpdateRequestId}
          activities={workshopUpdateRequestQuery.data?.activities}
          queryKey={useWorkshopUpdateRequestQuery.getKey(workshopUpdateRequestId)}
          resourceKey={resourceKey}
          workshopName={workshopUpdateRequestQuery.data?.workshop?.name}
        />
      </ContentLayout>
    </>
  );
};

export default WorkshopUpdateRequestDetailPage;
